import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Input } from "#components/atoms/Input/Input";
import { InputSuffix } from "#components/atoms/InputSuffix/InputSuffix";
import { Field } from "#components/molecules/Field/Field";
import { Modal } from "#components/molecules/Modal/Modal";
import { Select } from "#components/molecules/Select/Select";
import { getSurfaceTreatmentPriceSuffix } from "#modules/company/view/surfaceTreatmentLabels";
import {
  AddProcessStepFormSchema,
  AddProcessStepFormValues,
} from "#modules/onboardingV2/domain/addProcessStepFormSchema";
import { type CreateOnboardingProcessStepDto } from "#modules/onboardingV2/infra/createOnboardingProcessStep.api";
import { DurationCell } from "#modules/onboardingV2/view/processStepConfiguration/DurationCell";
import { DurationModal } from "#modules/onboardingV2/view/processStepConfiguration/DurationModal";
import {
  processStepIndustrialProcessOptions,
  processStepMethodOptions,
  processStepTypeOptions,
} from "#modules/onboardingV2/view/processStepConfiguration/processStepConfigurationLabels";
import { useCreateOnboardingProcessStep } from "#modules/onboardingV2/view/processStepConfiguration/useCreateOnboardingProcessStep";
import { useOnboardingAnalytics } from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { AnalyticsEvent } from "#shared/useAnalytics";
import {
  resolveOnboardingProcessStepTarget,
  SurfaceTreatmentMethodSchema,
  SurfaceTreatmentTypeSchema,
} from "shared";

type Props = {
  opened: boolean;
  onClose: () => void;
};

type DurationModalField = "preparationTimeFormula" | "cycleTimeFormula";

const DEFAULT_VALUES: AddProcessStepFormValues = {
  industrialProcess: null,
  type: SurfaceTreatmentTypeSchema.enum.INHOUSE,
  machineName: "",
  method: null,
  price: "",
  preparationTimeFormula: null,
  cycleTimeFormula: null,
};

export const AddProcessStepModal = ({ opened, onClose }: Props) => {
  const { t } = useTranslation();
  const { createOnboardingProcessStep, isPending } = useCreateOnboardingProcessStep();
  const { captureOnboardingEvent } = useOnboardingAnalytics("processStepConfiguration");

  const { control, handleSubmit, reset, setValue } = useForm<
    AddProcessStepFormValues,
    unknown,
    CreateOnboardingProcessStepDto
  >({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(AddProcessStepFormSchema),
  });

  const industrialProcess = useWatch({ control, name: "industrialProcess" });
  const type = useWatch({ control, name: "type" });
  const method = useWatch({ control, name: "method" });
  const preparationTimeFormula = useWatch({ control, name: "preparationTimeFormula" });
  const cycleTimeFormula = useWatch({ control, name: "cycleTimeFormula" });
  const target = resolveOnboardingProcessStepTarget(industrialProcess, type);
  const isMethodLocked = target === "MACHINE";
  const effectiveMethod = isMethodLocked ? SurfaceTreatmentMethodSchema.enum.time : method;
  const isTimeBased = effectiveMethod === SurfaceTreatmentMethodSchema.enum.time;

  const [openDurationField, setOpenDurationField] = useState<DurationModalField | null>(null);

  useEffect(() => {
    if (isMethodLocked) {
      setValue("method", SurfaceTreatmentMethodSchema.enum.time);
    }
  }, [isMethodLocked, setValue]);

  const industrialProcessOptions = processStepIndustrialProcessOptions();
  const typeOptions = processStepTypeOptions(t);
  const methodOptions = processStepMethodOptions(t);
  const priceUnit = effectiveMethod ? getSurfaceTreatmentPriceSuffix(t, effectiveMethod) : "€";

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    setOpenDurationField(null);
    onClose();
  };

  const onSubmit = (data: CreateOnboardingProcessStepDto) => {
    createOnboardingProcessStep(data, {
      onSuccess: () => {
        captureOnboardingEvent(AnalyticsEvent.OnboardingProcessStepAdded, {
          industrialProcess: data.industrialProcess,
        });
        handleClose();
      },
    });
  };

  const durationModalTitle = openDurationField
    ? t(`onboardingV2.processStepConfiguration.columns.${openDurationField}`)
    : "";
  const durationModalValue =
    openDurationField === "preparationTimeFormula" ? preparationTimeFormula : cycleTimeFormula;

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={t("onboardingV2.processStepConfiguration.addModal.title")}
        closeText={t("common.cancel")}
        primaryText={t("common.add")}
        onPrimary={handleSubmit(onSubmit)}
        primaryLoading={isPending}
      >
        <Controller
          name="industrialProcess"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label={t("onboardingV2.processStepConfiguration.columns.industrialProcess")}
              error={fieldState.error ? t("common.requiredField") : undefined}
            >
              <Select
                value={field.value}
                onChange={field.onChange}
                data={industrialProcessOptions}
                placeholder={t(
                  "onboardingV2.processStepConfiguration.addModal.industrialProcessPlaceholder",
                )}
                error={Boolean(fieldState.error)}
                aria-label={t("onboardingV2.processStepConfiguration.columns.industrialProcess")}
              />
            </Field>
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field, fieldState }) => (
            <Field label={t("onboardingV2.processStepConfiguration.columns.type")}>
              <Select
                value={field.value}
                onChange={(value) => value && field.onChange(value)}
                data={typeOptions}
                clearable={false}
                error={Boolean(fieldState.error)}
                aria-label={t("onboardingV2.processStepConfiguration.columns.type")}
              />
            </Field>
          )}
        />
        <Controller
          name="machineName"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label={t("onboardingV2.processStepConfiguration.columns.machine")}
              error={fieldState.error ? t("common.requiredField") : undefined}
            >
              <Input
                value={field.value}
                onChange={field.onChange}
                placeholder={t("onboardingV2.processStepConfiguration.columns.machinePlaceholder")}
                error={Boolean(fieldState.error)}
                aria-label={t("onboardingV2.processStepConfiguration.columns.machine")}
              />
            </Field>
          )}
        />
        <Controller
          name="method"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label={t("onboardingV2.processStepConfiguration.columns.method")}
              error={fieldState.error ? t("common.requiredField") : undefined}
            >
              <Select
                value={field.value}
                disabled={isMethodLocked}
                onChange={field.onChange}
                data={methodOptions}
                placeholder={t("onboardingV2.processStepConfiguration.addModal.methodPlaceholder")}
                error={Boolean(fieldState.error)}
                aria-label={t("onboardingV2.processStepConfiguration.columns.method")}
              />
            </Field>
          )}
        />
        <Controller
          name="price"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label={t("onboardingV2.processStepConfiguration.columns.price")}
              error={fieldState.error ? t("common.requiredField") : undefined}
            >
              <InputSuffix
                value={field.value}
                onChange={field.onChange}
                disabled={!effectiveMethod}
                placeholder="0,00"
                suffix={priceUnit}
                error={Boolean(fieldState.error)}
                aria-label={t("onboardingV2.processStepConfiguration.columns.price")}
              />
            </Field>
          )}
        />
        <Field label={t("onboardingV2.processStepConfiguration.columns.preparationTimeFormula")}>
          <DurationCell
            value={preparationTimeFormula}
            editable={isTimeBased}
            title={t("onboardingV2.processStepConfiguration.columns.preparationTimeFormula")}
            onOpen={() => setOpenDurationField("preparationTimeFormula")}
          />
        </Field>
        <Field label={t("onboardingV2.processStepConfiguration.columns.cycleTimeFormula")}>
          <DurationCell
            value={cycleTimeFormula}
            editable={isTimeBased}
            title={t("onboardingV2.processStepConfiguration.columns.cycleTimeFormula")}
            onOpen={() => setOpenDurationField("cycleTimeFormula")}
          />
        </Field>
      </Modal>

      <DurationModal
        opened={openDurationField !== null}
        title={durationModalTitle}
        value={durationModalValue}
        onClose={() => setOpenDurationField(null)}
        onSave={(value) => {
          if (openDurationField) {
            setValue(openDurationField, value);
          }
          setOpenDurationField(null);
        }}
      />
    </>
  );
};
